/**
 * The application shell: left navigation, live readiness, signed-in identity.
 *
 * # Why the navigation is grouped this way
 *
 * `ai-security-ops` groups its nav into the questions a user actually asks, in order, and that
 * grouping is the part worth carrying over — it makes each screen's *purpose* legible from where
 * it sits, so "captures" and "telemetry" stop looking like two words for the same thing.
 *
 * Conduit's ordering is its own, because Conduit answers a different sequence. It is the spine
 * from `docs/DESIGN.md` §2: a ticket arrives, the estate says what the ticket concerns, evidence
 * brackets the work, exposure says what is wrong, and access is how anyone reaches a device to fix
 * it. A reader who follows the nav top to bottom has walked the product.
 *
 * Sections whose features are not built yet are absent rather than disabled. A greyed-out item is
 * a promise, and a shell full of promises trains people to ignore the nav.
 */

import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { JSX } from 'react';

import { ThemePicker } from './ThemePicker';
import { LayoutPicker, useLayoutPreference } from './LayoutPicker';
import { api, type ReadinessResponse, type WhoAmI } from '../shared/api/client';
import './layout.css';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
}

interface NavSection {
  title: string | null;
  hint?: string;
  items: NavItem[];
}

const NAV: NavSection[] = [
  {
    title: null,
    items: [{ to: '/', label: 'Overview', icon: '▚', end: true }],
  },
  {
    title: 'Work',
    hint: 'what needs doing',
    items: [
      { to: '/tickets', label: 'Tickets', icon: '◫' },
      { to: '/sla', label: 'Service Levels', icon: '◴' },
      { to: '/ingestion', label: 'Ingestion', icon: '⤓' },
    ],
  },
  {
    title: 'Estate',
    hint: 'what I have',
    items: [
      { to: '/cis', label: 'Configuration Items', icon: '▤' },
      { to: '/captures', label: 'Configuration', icon: '▦' },
      { to: '/telemetry', label: 'Telemetry', icon: '∿' },
    ],
  },
  {
    title: 'Exposure',
    hint: "what's wrong",
    items: [
      { to: '/vulnerabilities', label: 'Vulnerabilities', icon: '⚠' },
      { to: '/shadow-assets', label: 'Shadow Assets', icon: '◌' },
    ],
  },
  {
    title: 'Risk',
    hint: 'how bad · what first',
    items: [
      { to: '/leaderboard', label: 'Fix First', icon: '⚖' },
      { to: '/campaigns', label: 'Patching Schedule', icon: '◷' },
      { to: '/risk-config', label: 'Methodology', icon: '⚙' },
    ],
  },
  {
    title: 'Access',
    hint: 'how we reach it',
    items: [
      { to: '/playbooks', label: 'Playbooks', icon: '☷' },
      { to: '/access-requests', label: 'Access Requests', icon: '⚷' },
      { to: '/contracts', label: 'Session Contracts', icon: '✍' },
      { to: '/collectors', label: 'Collectors', icon: '⇲' },
      { to: '/audit', label: 'Audit Ledger', icon: '⛓' },
    ],
  },
  {
    title: 'Platform',
    hint: 'is it working',
    items: [
      { to: '/ops', label: 'Operational Health', icon: '◉' },
      { to: '/administration', label: 'Administration', icon: '⚙' },
      { to: '/preferences', label: 'Preferences', icon: '☼' },
    ],
  },
];

export function Layout({ principal }: { principal: WhoAmI }): JSX.Element {
  const [readiness, setReadiness] = useState<ReadinessResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .readiness()
      .then((r) => {
        if (!cancelled) setReadiness(r);
      })
      .catch(() => {
        if (!cancelled) setReadiness(null);
      });
    return (): void => {
      cancelled = true;
    };
  }, []);

  const healthy = readiness?.status === 'ready';

  // Applies the stored layout preference and follows device capability changes.
  useLayoutPreference();

  // Closed by default. On a phone the rail is a drawer; on a desktop this state is ignored
  // entirely by CSS, so there is one component rather than two that drift.
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Close on navigation. Leaving the drawer open over the page somebody just asked for is the
  // single most irritating bug in a mobile nav, and it is always this.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  return (
    <div className={navOpen ? 'shell shell--nav-open' : 'shell'}>
      {/* Small screens only. A phone cannot afford a permanent 15rem rail, and stacking the rail
          above the content means scrolling past fourteen links to reach anything. */}
      <header className="topbar">
        <button
          type="button"
          className="topbar__toggle"
          aria-expanded={navOpen}
          aria-controls="conduit-nav"
          onClick={() => {
            setNavOpen((open) => !open);
          }}
        >
          <span aria-hidden="true">{navOpen ? '\u2715' : '\u2261'}</span>
          <span className="visually-hidden">{navOpen ? 'Close navigation' : 'Open navigation'}</span>
        </button>
        <span className="topbar__product">Conduit</span>
        <span
          className={healthy ? 'dot dot--ok' : 'dot dot--bad'}
          aria-hidden="true"
          title={healthy ? 'database and cache ok' : 'degraded'}
        />
      </header>

      {/* Tapping outside the drawer closes it. Rendered only when open so it cannot swallow
          clicks on a desktop, where the rail is permanent and there is no overlay. */}
      {navOpen && (
        <button
          type="button"
          className="nav__scrim"
          aria-label="Close navigation"
          onClick={() => {
            setNavOpen(false);
          }}
        />
      )}

      <aside className="nav" id="conduit-nav">
        <div className="nav__brand">
          <div className="nav__mark">C</div>
          <div>
            <div className="nav__product">Conduit</div>
            <div className="nav__tagline">ticketed · evidenced · time-boxed</div>
          </div>
        </div>

        <nav className="nav__sections">
          {NAV.map((section, i) => (
            <div className="nav__section" key={section.title ?? `top-${String(i)}`}>
              {section.title !== null && (
                <div className="nav__section-head">
                  <span className="nav__section-title">{section.title}</span>
                  {section.hint !== undefined && (
                    <span className="nav__section-hint">{section.hint}</span>
                  )}
                </div>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end ?? false}
                  className={({ isActive }) => (isActive ? 'nav__item nav__item--active' : 'nav__item')}
                >
                  <span className="nav__icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="nav__foot">
          <div className="nav__who">
            <div className="nav__whoami">{principal.roles.join(' · ') || 'no roles'}</div>
            <button type="button" className="nav__signout" onClick={() => void signOut()}>
              sign out
            </button>
          </div>
          <div className="nav__status">
            {/* Dependency health, not process health. A control plane whose database is gone still
                answers /api/health, and an operator needs to see the difference. */}
            <span className={healthy ? 'dot dot--ok' : 'dot dot--bad'} aria-hidden="true" />
            {readiness === null
              ? 'checking…'
              : healthy
                ? 'database · cache ok'
                : `degraded: ${readiness.database} / ${readiness.cache}`}
          </div>
          <ThemePicker />
          <LayoutPicker />
        </div>
      </aside>

      <main className="content">
        <Outlet />
      </main>
    </div>
  );
}

async function signOut(): Promise<void> {
  try {
    await api.signOut();
  } finally {
    // A full reload rather than a route change: it drops every cached fetch and component state
    // belonging to the session that just ended, which is the behavior you want from sign-out.
    window.location.assign('/');
  }
}
